import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CostCenter } from '../../../models/CostCenterModel';
import showToast from '../../../utils/toast';
import CostCenterForm from '../../../components/Companies/CostCenterForm';
import ButtonCancelForm from '../../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../../components/Base/ConfirmAlert';
import useCostCenterService from '../../../services/useCostCenterService';
import CostCenterTable from './CostCenterTable';
import useCompaniesService from '../../../services/useCompaniesService';
import { useFormErrors } from '../../../hooks/useFormErrors';
import Pagination from '../../../components/Base/Pagination';
import TableTextSearch from '../../../components/Base/TableTextSearch';
import MaintainerPageShell from '../../../components/Base/MaintainerPageShell';
import { Calculator, Plus } from 'lucide-react';

const CostCenterPage: React.FC = () => {
  const svc = useCostCenterService();
  const {
    list: getCostCenters,
    store: createCostCenter,
    update: updateCostCenter,
    remove: removeCostCenter,
    fetching,
  } = svc;
  const companiesSvc = useCompaniesService();
  const { list: listCompanies } = companiesSvc;
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CostCenter | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [costCenterIdToDelete, setCostCenterIdToDelete] = useState<number | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });

  const [searchCode, setSearchCode] = useState('');
  const searchCodeRef = useRef(searchCode);
  searchCodeRef.current = searchCode;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCostCenters = useCallback((page: number = 1, code?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (code !== undefined ? code : searchCodeRef.current).trim();
    if (term) filters.search = term;

    getCostCenters(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp) => {
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.cost_centers) ? data.cost_centers : data.items ?? [];
          setCostCenters(items);

          if (data.pagination) {
            setPagination({
              page: data.pagination.page || page,
              totalPages: data.pagination.total_pages || 1,
              totalCount: data.pagination.total_count || 0,
              pageSize: data.pagination.page_size || 10,
              hasNext: data.pagination.has_next || false,
              hasPrevious: data.pagination.has_previous || false,
            });
          }
        },
        onError: (resp) => {
          if (resp?.http_status === 403) {
            showToast(
              resp?.message || 'No tienes permisos para acceder a esta sección.',
              'error'
            );
          } else {
            showToast(resp?.message ?? 'Error cargando centros de costo', 'error');
          }
        },
      },
      ctrl.signal
    );
  }, [getCostCenters]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchCode(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadCostCenters(1, value);
    }, 400);
  }, [loadCostCenters]);

  const loadCompanies = useCallback(() => {
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    listCompanies({}, {
      onSuccess: (resp: any) => {
        const items = resp?.data.companies ?? [];
        const mapped = items.map((c: any) => ({
          value: c.id,
          label: c.name ?? c.company_name ?? 'Sin nombre',
        }));
        setCompanies(mapped);
      },
      onError: () => showToast('Error cargando empresas', 'error'),
    }, ctrl.signal);
  }, [listCompanies]);

  useEffect(() => {
    loadCostCenters();
    loadCompanies();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [loadCostCenters, loadCompanies]);

  const openCreate = () => {
    svc.create({
      onSuccess: (resp: any) => {
        const list = resp?.data ?? [];
        const mapped = list.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        }));
        setCompanies(mapped);

        setEditing({
          name: '',
          code: '',
          company: 0,
        });

        setShowForm(true);
      },
      onError: () => showToast('Error cargando formulario', 'error'),
    });
  };

  const openEdit = (c: CostCenter) => {
    svc.edit(c.id!, {
      onSuccess: (resp: any) => {
        const data = resp.data ?? {};
        const costCenter = data.cost_center ?? {};
        const companiesList = data.companies ?? [];

        const normalized = {
          ...costCenter,
          company: costCenter.company_id ?? costCenter.company ?? 0,
        };

        setEditing(normalized);
        setCompanies(
          companiesList.map((item: any) => ({
            value: String(item.id),
            label: item.name,
          }))
        );
        setShowForm(true);
      },
      onError: () => showToast('Error cargando datos del centro de costo', 'error'),
    });
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);

    try {
      const payload = { code: editing.code, company: editing.company };
      if (editing.id) {
        const r = await updateCostCenter(editing.id, payload);
        return r;
      } else {
        const r = await createCostCenter(payload);
        return r;
      }
    } catch (error: any) {
      const fields = error?.response?.data;

      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }

      setErrorsFromResponse(fields);
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      loadCostCenters(pagination.page, searchCodeRef.current.trim() || undefined);
      return;
    }
    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const openDeleteConfirmation = (id: number) => {
    setCostCenterIdToDelete(id);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = () => {
    if (costCenterIdToDelete === null) return;

    removeCostCenter(costCenterIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        loadCostCenters(pagination.page, searchCodeRef.current.trim() || undefined);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setCostCenterIdToDelete(null);
  };

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setCostCenterIdToDelete(null);
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || costCenters.length}
        </span>
        centros de costo
      </span>
    );

  return (
    <MaintainerPageShell
      title="Centros de costo"
      icon={<Calculator size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo centro de costo
        </button>
      }
    >
      <CostCenterTable
        data={costCenters}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={pagination.totalCount}
        searchTerm={searchCode}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchCode}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre o código..."
            aria-label="Buscar centros de costo"
          />
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                loadCostCenters(page, searchCodeRef.current.trim() || undefined);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="companies-modal">
          <div className="companies-modal-inner">
            <h3>{editing.id ? 'Editar centro de costo' : 'Crear centro de costo'}</h3>
            <CostCenterForm
              value={editing}
              onChange={setEditing}
              error={fieldError || undefined}
              companies={companies}
              fieldErrors={fieldErrors}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar centro de costo?"
        message="Esta acción es permanente. El centro de costo dejará de mostrarse en la lista."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />
    </MaintainerPageShell>
  );
};

export default CostCenterPage;
